import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, zip } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { RecruitingDealsService } from 'src/app/recruiting/deals/deals.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ListItem } from 'src/app/shared/models/list-item.model';
import {
    Attachment,
    CandidateOfferClientNote,
    DeclineReasons,
    OfferNotes,
    VendorOptions
} from 'src/app/shared/models/offers.model';
import { FileValidationResult, SharedOffersService as OfferService } from 'src/app/shared/offers-shared/offers.service';
import { downloadBlob } from 'src/app/shared/utilities/download-blob';
import { RecruitingActions } from '../actions';
import { ToasterService } from 'src/app/shared/services/toaster.service';

@Injectable()
export class DealsDetailsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _offersService: OfferService,
        private readonly _dealsService: RecruitingDealsService,
        private readonly _store: Store<{}>,
        private readonly _recruitingDealsService: RecruitingDealsService,
        private readonly _toasterService: ToasterService
    ) {}

    getDetails$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                RecruitingActions.DealDetailsActions.getDetails,
                RecruitingActions.DealDetailsActions.declineOfferSuccess
            ),
            switchMap((action: any) =>
                zip(
                    this._recruitingDealsService.getOffer(action.id),
                    this._recruitingDealsService.getOfferMarginDifferences(action.id)
                ).pipe(
                    map(([details, difference]) =>
                        RecruitingActions.DealDetailsActions.getDetailsSuccess({ details, difference })
                    ),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.getDetailsFailure({ error })))
                )
            )
        )
    );

    getOfferNotes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                RecruitingActions.DealDetailsActions.getOfferNotes,
                RecruitingActions.DealDetailsActions.getDetails,
                RecruitingActions.DealDetailsActions.postOfferNoteAndNotifyAMSuccess,
                RecruitingActions.DealDetailsActions.declineOfferSuccess
            ),
            switchMap((action: any) =>
                this._recruitingDealsService.getOfferNotes(action.id).pipe(
                    map((notes: OfferNotes[]) => RecruitingActions.DealDetailsActions.getOfferNotesSuccess({ notes })),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.getOfferNotesFailure({ error })))
                )
            )
        )
    );

    postOfferNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.postOfferNoteAndNotifyAM),
            switchMap((action: { id: number; note: CandidateOfferClientNote }) =>
                this._recruitingDealsService.postOfferNoteAndNotifyAM(action.id, action.note).pipe(
                    map((result: any) =>
                        RecruitingActions.DealDetailsActions.postOfferNoteAndNotifyAMSuccess({ id: action.id })
                    ),
                    catchError((error) =>
                        of(RecruitingActions.DealDetailsActions.postOfferNoteAndNotifyAMFailure({ error }))
                    )
                )
            )
        )
    );

    getAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                RecruitingActions.DealDetailsActions.getAttachments,
                RecruitingActions.DealDetailsActions.getDetails,
                RecruitingActions.DealDetailsActions.postAttachmentSuccess,
                RecruitingActions.DealDetailsActions.updateAttachmentSuccess,
                RecruitingActions.DealDetailsActions.deleteAttachmentSuccess
            ),
            switchMap((action: any) =>
                this._offersService.getAttachments(action.id).pipe(
                    map((attachments: Attachment[]) =>
                        RecruitingActions.DealDetailsActions.getAttachmentsSuccess({ attachments })
                    ),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.getAttachmentsFailure({ error })))
                )
            )
        )
    );

    getAttachmentFile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.getAttachmentBlob),
            switchMap((action: any) =>
                this._offersService.getAttachmentBlob(action.offerId, action.file.id).pipe(
                    map((blob: Blob) => {
                        downloadBlob(blob, action.file.fileName);
                        return RecruitingActions.DealDetailsActions.getAttachmentBlobSuccess({
                            attachment: blob,
                            file: action.file
                        });
                    }),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.getAttachmentBlobFailure({ error })))
                )
            )
        )
    );

    postAttachment$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.postAttachment),
            switchMap((action: any) =>
                this._offersService.postAttachment(action.offerId, action.fileToUpload).pipe(
                    map(() =>
                        RecruitingActions.DealDetailsActions.postAttachmentSuccess({
                            id: action.offerId,
                            fileName: action.fileToUpload.name
                        })
                    ),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.postAttachmentFailure({ error })))
                )
            )
        )
    );

    updateAttachment$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.updateAttachment),
            switchMap((action: any) =>
                this._offersService.updateAttachment(action.offerId, action.attachmentId, action.fileToUpload).pipe(
                    map((result: FileValidationResult[]) =>
                        RecruitingActions.DealDetailsActions.updateAttachmentSuccess({
                            id: action.offerId,
                            fileName: action.fileToUpload.name
                        })
                    ),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.updateAttachmentFailure({ error })))
                )
            )
        )
    );

    deleteAttachment$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.deleteAttachment),
            switchMap((action: any) =>
                this._offersService.deleteAttachment(action.offerId, action.id).pipe(
                    map(() => RecruitingActions.DealDetailsActions.deleteAttachmentSuccess({ id: action.offerId })),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.deleteAttachmentFailure({ error })))
                )
            )
        )
    );
    getDeclineReasons$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.getDeclineReasons),
            switchMap(() => {
                return this._dealsService.getDeclineReasons().pipe(
                    map((declineReasons: DeclineReasons[]) =>
                        RecruitingActions.DealDetailsActions.getDeclineReasonsSuccess({ declineReasons })
                    ),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.getDeclineReasonsFailure({ error })))
                );
            })
        )
    );

    getVendorSearch$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.vendorSearch),
            switchMap((action: any) =>
                this._dealsService.getVendorOptions(action.nameSearch).pipe(
                    map((vendorSearch: VendorOptions[]) => {
                        vendorSearch.push({ id: 0, legalName: 'Other', operatingName: 'Other' });
                        return RecruitingActions.DealDetailsActions.vendorSearchSuccess({ vendorSearch });
                    }),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.vendorSearchFailure({ error })))
                )
            )
        )
    );

    getFacilitySearch$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.facilitySearch),
            switchMap((action: any) =>
                this._dealsService.getFacilityOptions(action.nameSearch).pipe(
                    map((facilitySearch: ListItem[]) => {
                        facilitySearch.push({ id: 0, name: 'Other' });
                        return RecruitingActions.DealDetailsActions.facilitySearchSuccess({ facilitySearch });
                    }),
                    catchError((error) => of(RecruitingActions.DealDetailsActions.facilitySearchFailure({ error })))
                )
            )
        )
    );

    declineOffer$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.declineOffer),
            switchMap((action: any) =>
                this._dealsService.putDeclineOffer(action.offerId, action.data).pipe(
                    map(() => {
                        this._store.dispatch(
                            showToasterMessage({
                                message: `Offer declined successfully...`,
                                duration: 5000
                            })
                        );
                        return RecruitingActions.DealDetailsActions.declineOfferSuccess({ id: action.offerId });
                    }),
                    catchError((error) => {
                        this._store.dispatch(
                            showToasterMessage({
                                message: `Error declining the offer: ${error}`,
                                duration: 5000
                            })
                        );
                        return of(RecruitingActions.DealDetailsActions.declineOfferFailure({ error }));
                    })
                )
            )
        )
    );

    // declineOffer$ = createEffect(() => this._actions$.pipe(
    //     ofType(
    //         RecruitingActions.DealsActions.declineOffer),
    // 	switchMap((action) => this._offersService.postAttachment(action.offerId)
    //     .pipe(
    //         map((result) => RecruitingActions.DealsActions.declineOfferSuccess()),
    //         catchError((error) => of(RecruitingActions.DealsActions.declineOfferFailure({error})))
    //     ))
    // ));

    showAttachmentSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                RecruitingActions.DealDetailsActions.postAttachmentSuccess,
                RecruitingActions.DealDetailsActions.updateAttachmentSuccess
            ),
            map((action: any) => showToasterMessage({ message: `${action.fileName} has been uploaded.` }))
        )
    );

    showAttachmentDeleteMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.deleteAttachmentSuccess),
            map(() => showToasterMessage({ message: `Attachment has been deleted.` }))
        )
    );

    showAttachmentDownloadMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.getAttachmentBlobSuccess),
            map((action: any) => showToasterMessage({ message: `${action.file.fileName} is downloading.` }))
        )
    );

    setOfferAsVerballyAccepted$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealDetailsActions.setOfferDetailAsVerballyAccepted),
            switchMap((action) => {
                return this._offersService.putToVerballyAcceptedStatus(action.offerId, true).pipe(
                    map(() =>
                        RecruitingActions.DealDetailsActions.setOfferDetailAsVerballyAcceptedSuccess({
                            offerId: action.offerId
                        })
                    ),
                    catchError(() => of(RecruitingActions.DealDetailsActions.setOfferDetailAsVerballyAcceptedFailure()))
                );
            })
        );
    });

    verbalAcceptanceSuccess$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealDetailsActions.setOfferDetailAsVerballyAcceptedSuccess),
                tap(() => {
                    this._toasterService.toast('The verbal acceptance of the offer has been shared');
                })
            );
        },
        { dispatch: false }
    );

    verbalAcceptanceFailure$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealDetailsActions.setOfferDetailAsVerballyAcceptedFailure),
                tap(() => {
                    this._toasterService.toast('Error occurred trying to set offer as verbally accepted', 7000);
                })
            );
        },
        { dispatch: false }
    );
}
